import { computed } from 'vue';
import useUserService from '@/composables/apiServices/useUserService';
import type {
  IAppearanceAttributesForm,
  IAppearanceSettingsForm
} from '@/types/composables/useAppearance';
import { EAppearanceField } from '@/types/composables/useAppearance.enums';
import { enabledThemesNames, getThemeMode } from '@/helpers/theme';
import type { SelectedColor } from '@/types/services/user.service';
import { EContactOrdering, EFolderListSize } from '@/types/services/user.service.enums';

const useAppearance = () => {
  const { details, updateDetails, attributes, updateAttributes } = useUserService();
  const { mutateAsync: updateUserDetails } = updateDetails();
  const { data: userDetails } = details();
  const { mutateAsync: updateUserAttributes } = updateAttributes();
  const { data: attributesData } = attributes();

  const detailsForm = computed<IAppearanceSettingsForm>({
    get: () => ({
      [EAppearanceField.DARK_MODE]: getThemeMode(userDetails.value?.meta.ui),
      [EAppearanceField.READING_PANE]: userDetails.value?.view_mode ?? '2-panel',
      [EAppearanceField.DISPLAY_DENSITY]: userDetails.value?.meta?.displayDensity ?? 'default',
      [EAppearanceField.INBOX_TYPE]: userDetails.value?.meta?.inboxType ?? 'default',
      [EAppearanceField.SELECTED_COLOR]:
        userDetails.value?.meta?.ui?.color ??
        ((import.meta.env.VITE_DEFAULT_THEME ?? enabledThemesNames[0] ?? 'blue') as SelectedColor),
      [EAppearanceField.CONTACT_SHOWN]: attributesData.value?.contactAccordionShownState ?? true
    }),
    set: async (newValue) => {
      updateUserDetails({
        view_mode: newValue[EAppearanceField.READING_PANE],
        ui: {
          theme: newValue[EAppearanceField.DARK_MODE],
          color: newValue[EAppearanceField.SELECTED_COLOR]
        },
        displayDensity: newValue[EAppearanceField.DISPLAY_DENSITY],
        inboxType: newValue[EAppearanceField.INBOX_TYPE]
      });
    }
  });
  const attributesForm = computed<IAppearanceAttributesForm>({
    get: () => ({
      [EAppearanceField.CONTACT_SHOWN]: attributesData.value?.contactAccordionShownState ?? true,
      [EAppearanceField.THREADING_SHOWN]: attributesData.value?.threadingShownState ?? true,
      [EAppearanceField.CONTACT_ORDERING]:
        attributesData.value?.contactOrdering ?? EContactOrdering.NAME,
      [EAppearanceField.FOLDER_LIST_SIZE]:
        (attributesData.value?.folderListSize as EFolderListSize) ?? EFolderListSize.DEFAULT
    }),
    set: async (newValue) => {
      updateUserAttributes({
        contactAccordionShownState: newValue[EAppearanceField.CONTACT_SHOWN],
        threadingShownState: newValue[EAppearanceField.THREADING_SHOWN],
        contactOrdering: newValue[EAppearanceField.CONTACT_ORDERING],
        folderListSize: newValue[EAppearanceField.FOLDER_LIST_SIZE]
      });
    }
  });

  // Extra helper function used to make updating the settings form easier
  const handleDetailsChange = <T extends keyof IAppearanceSettingsForm>(
    key: T,
    value: IAppearanceSettingsForm[T]
  ) => {
    detailsForm.value = { ...detailsForm.value, [key]: value };
  };

  // Extra helper function used to make updating the attributes form easier
  const handleAttributesChange = <T extends keyof IAppearanceAttributesForm>(
    key: T,
    value: IAppearanceAttributesForm[T]
  ) => {
    attributesForm.value = { ...attributesForm.value, [key]: value };
  };

  return {
    handleDetailsChange,
    handleAttributesChange,
    detailsForm,
    attributesForm
  };
};

export default useAppearance;
